<script lang="ts">
  // One company page.
  //
  // Two rules are load-bearing here and neither asks the reader for attention:
  //
  //  1. The first line of the bar is the COMPANY's name, drawn by us. The page's own
  //     title goes on the second line. A page can therefore never answer "who is
  //     talking" with anything but the truth.
  //  2. Opening always attempts a fetch. If that fails the cached page still renders —
  //     reading a catalogue on a train is the ordinary case — but every control on it is
  //     dead. The seller is the only source of truth about price, so reading a stale one
  //     is harmless and ordering against it is not.

  import { contactById, mutedCategories } from '$lib/db/contacts';
  import type { ContactRow, ViewRow } from '$lib/db/db';
  import { latestCartsFor } from '$lib/db/threads';
  import { elementsOf, loadView } from '$lib/db/views';
  import { formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { queueCartResponse, queueMessage } from '$lib/protocol/outbox';
  import {
    answerElementJson,
    requestElementJson,
    type CalendarEventElement,
    type CartElement,
    type MessageElement,
  } from '$lib/protocol/types';
  import { live } from '$lib/state/live';

  import { goBack } from '../../app/router.svelte';
  import type { ElementContext } from '../elements/context';
  import ElementView from '../elements/ElementView.svelte';
  import EmptyState from '../parts/EmptyState.svelte';

  let { accountId, viewId }: { accountId: string; viewId: string } = $props();

  const contact = live<ContactRow | undefined>(() => contactById(accountId), undefined);
  const muted = live<Set<string>>(() => mutedCategories(accountId), new Set<string>());
  // The conversation is the live record of a cart; a cart on a page is a snapshot of
  // one. A snapshot may be acted on only while it still matches — otherwise the buyer
  // would be ordering against a version the company has already replaced.
  const threadCarts = live<Map<string, CartElement>>(
    () => latestCartsFor(accountId),
    new Map<string, CartElement>(),
  );

  let row = $state<ViewRow | undefined>(undefined);
  let fresh = $state(false);
  let loading = $state(true);

  $effect(() => {
    let alive = true;
    loading = true;
    void loadView(accountId, viewId).then((result) => {
      if (!alive) {
        return;
      }
      row = result.row;
      fresh = result.fresh;
      loading = false;
    });
    return () => {
      alive = false;
    };
  });

  const elements = $derived<MessageElement[]>(elementsOf(row));

  const staleCarts = $derived.by(() => {
    const out = new Set<string>();
    for (const element of elements) {
      if (element.type !== 'cart') {
        continue;
      }
      const live = $threadCarts.get(element.cartId);
      // No copy in the conversation means nothing to check against, and a cart the
      // buyer cannot see the current state of is not one to act on.
      if (live === undefined || JSON.stringify(live) !== JSON.stringify(element)) {
        out.add(element.cartId);
      }
    }
    return out;
  });

  const companyName = $derived(
    $contact === undefined
      ? ''
      : $contact.name.length > 0
        ? $contact.name
        : formatPhone($contact.phone),
  );

  // A page is not a conversation, so there is no read state and no question history to
  // consult: an answered question is answered in the thread, not here.
  const ctx = $derived<ElementContext>({
    accountId,
    sequence: $contact?.lastSequence ?? 0,
    disabled: !fresh || $contact === undefined || $contact.needsReverify,
    answeredQuestions: new Set<string>(),
    staleCarts,
    mutedCategories: $muted,
    // A control on a page binds to something on the same page. For a cart the
    // conversation is the authority, so the live copy wins where there is one — the
    // page's own copy is a snapshot and may already be out of date.
    resolveCart: (cartId) =>
      $threadCarts.get(cartId) ??
      elements.find((e): e is CartElement => e.type === 'cart' && e.cartId === cartId),
    resolveEvent: (eventId) =>
      elements.find(
        (e): e is CalendarEventElement => e.type === 'calendar_event' && e.eventId === eventId,
      ),
    onAnswer: (questionId, text) => {
      void queueMessage({ accountId, elements: [answerElementJson(questionId, text)] });
    },
    onRequestItems: (items) => {
      void queueMessage({ accountId, elements: [requestElementJson(items)] });
    },
    onCartRespond: (cartId, decision, items, note) => {
      void queueCartResponse({
        accountId,
        cartId,
        decision,
        items,
        ...(note !== undefined ? { note } : {}),
      });
    },
  });
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <div class="titles">
    <span class="who">{companyName}</span>
    <span class="what">{row?.title ?? ''}</span>
  </div>
</header>

{#if !loading && !fresh && row !== undefined}
  <!-- Not a warning to act on — a statement of what these controls are doing. The page
       is readable; the buttons are simply not live. -->
  <p class="offline">{t('views.offline')}</p>
{/if}

{#if loading && row === undefined}
  <p class="offline">{t('views.loading')}</p>
{:else if row === undefined || elements.length === 0}
  <EmptyState title={t('views.goneTitle')} body={t('views.goneBody')} />
{:else}
  <div class="page">
    {#each elements as element, i (i)}
      <div class="block"><ElementView {element} {ctx} /></div>
    {/each}
  </div>
{/if}

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  .titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .who {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .what {
    font-size: 13px;
    color: var(--ink-soft);
    line-height: 1.2;
  }
  .offline {
    margin: 0 16px 8px;
    font-size: 13px;
    color: var(--ink-soft);
  }
  .page {
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  .block {
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface);
    padding: 12px;
  }
</style>
